import { FUNNEL_SESSION_TOKEN_KEY } from '@/constants/funnel';
import authService from '@/services/auth.service';
import { getWorkosAccessToken, hasWorkosSession } from '@/services/workos-auth-bridge';
import { type InternalAxiosRequestConfig } from 'axios';

/**
 * Returns true if the outgoing request targets a funnel-session endpoint.
 * These are anonymous endpoints — auth flows (WorkOS, JWT, visitor token)
 * don't apply, and a 401 from them does NOT mean "redirect to sign-in"; it
 * means "the funnel session has expired/been revoked, start fresh."
 */
function isFunnelSessionRequest(url: string | undefined): boolean {
  if (!url) return false;
  return url.includes('/v1/intake/session');
}

/**
 * Attaches the best available auth token to outgoing requests.
 *
 * Resolution order:
 * 1. Funnel session token (anonymous pre-payment funnel; takes priority on
 *    funnel-session URLs so the funnel session is never confused with an
 *    authenticated session even if the user has a stale JWT in localStorage)
 * 2. WorkOS access token (AuthKit-managed, RS256)
 * 3. Legacy localStorage JWT (self-signed HS256)
 * 4. Visitor session token (legacy anonymous sessions for member-backend)
 */
export async function attachAuthToken(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  if (isFunnelSessionRequest(config.url)) {
    const funnelToken = localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
    if (funnelToken) {
      config.headers['X-Funnel-Session'] = funnelToken;
    }
    return config;
  }

  const workosToken = await getWorkosAccessToken();
  if (workosToken) {
    config.headers['authorization'] = `Bearer ${workosToken}`;
    return config;
  }

  const token = authService.getToken();
  if (token) {
    config.headers['authorization'] = `Bearer ${token}`;
    return config;
  }

  const visitorToken = authService.getVisitorToken();
  if (visitorToken) {
    config.headers['x-session-authorization'] = visitorToken;
  }

  return config;
}

/**
 * Returns true if a 401 from this request URL should NOT trigger the
 * sign-in redirect. Funnel-session endpoints sit pre-auth, so a 401 there is
 * an expected outcome of an expired/revoked anonymous token, not a hostile
 * unauthenticated state.
 */
export function shouldBypassRedirectForUrl(url: string | undefined): boolean {
  return isFunnelSessionRequest(url);
}

/**
 * Returns true if the current session should handle 401s by redirecting
 * to sign-in. WorkOS sessions should NOT redirect — the AuthKit provider
 * and WorkosAuthBridge component manage the unauthenticated state instead.
 */
export function shouldRedirectOn401(): boolean {
  return !hasWorkosSession();
}
